import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup
} from '@angular/forms';
import {
  CalculateFutureDatePayload,
  Leg,
  RfqLegSettings,
  ScheduleName
} from '@approot/models';
import { ScheduleColumn } from '@approot/models/schedule.model';
import { ProductsConfigService } from '@approot/services';
import { LegService } from '@approot/services/leg/leg.service';
import { DateUtilities, SubscriptionSink, Utilities } from '@approot/utilities';
import ScheduleUtilities from '@approot/utilities/schedule-utilities';
import copy from 'copy-to-clipboard';
import { LegScheduleService } from '../../leg/services/leg-schedule.service';

@Component({
  selector: 'app-schedule',
  templateUrl: './schedule.component.html',
  styleUrls: ['./schedule.component.scss']
})
export class ScheduleComponent implements OnInit, OnDestroy {
  @Input() public columns: ScheduleColumn[];
  @Input() public flows = []; // the flows are stored here
  @Input() public isModalOpened = false;
  @Input() public additionalColumns: any[];
  @Input() public legData: FormGroup;
  @Input() public rfqLegSettings: RfqLegSettings;
  @Input() public scheduleName: ScheduleName;
  @Input() public leg: Leg;
  @Output() public scheduleOpenChanged = new EventEmitter<boolean>();
  @Output() public fieldChanges = new EventEmitter<{
    changedAttributes: string[];
    scheduleName: string;
  }>(null);

  public originalFormData: any;
  public scheduleArray: FormArray;
  public filedOriginalFocusedValue: any; // stores the original value when a field is clicked
  public selectedFieldElem: any;
  public hoveredError: string;
  public totalNotional: number = NaN;
  public notionalCurrency = '';
  public showTotalNotional = true;
  private subSink = new SubscriptionSink();
  constructor(
    private elementRef: ElementRef,
    private legService: LegService,
    private renderer: Renderer2,
    private productsConfigService: ProductsConfigService,
    private legScheduleService: LegScheduleService
  ) {}

  public ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  public ngOnInit(): void {
    // we want to re-make the form control schedule, as we do not want changes
    // to reflect on the leg
    const schedule = this.legData
      .get(this.scheduleName)
      .value.map((row, index) => {
        const formGroup = ScheduleUtilities.scheduleControlMaker(
          row,
          index,
          this.legData,
          this.rfqLegSettings.rfqLeg,
          this.leg.productType,
          this.columns,
          this.legData.get(this.scheduleName) as FormArray
        );

        this.controlListeners(formGroup, index);
        return formGroup;
      });
    this.scheduleArray = new FormArray(schedule);
    this.calcTotalNotional();
    this.notionalCurrency = this.legData.value['currency'];
    this.legScheduleService.setScheduleValidators(
      this.scheduleName,
      this.scheduleArray
    );
    this.originalFormData = this.legData.getRawValue();
    this.showTotalNotional =
      this.scheduleName === 'schedule' && this.originalFormData.cross;
    if (this.showTotalNotional) {
      this.subSink.sink = this.scheduleArray.valueChanges.subscribe(() => {
        this.calcTotalNotional();
      });
    }
  }

  public get scheduleArrayControls() {
    return this.scheduleArray.controls as FormGroup[];
  }

  private calcTotalNotional() {
    if (!this.showTotalNotional) {
      return;
    }
    try {
      this.totalNotional = this.scheduleArray.controls
        .map((s: FormGroup) =>
          Number(s.controls.notional.value.replace(/,/g, ''))
        )
        .reduce((a, b) => a + b, 0);
    } catch (e) {
      console.error(e);
      this.totalNotional = NaN;
    }
  }

  private controlListeners(formGroup: FormGroup, index: number): void {
    Object.keys(formGroup.controls).forEach((key: string) => {
      this.subSink.sink = formGroup
        .get(key)
        .valueChanges.subscribe(val =>
          this.formatInputText(key, val, index, false)
        );
    });
  }

  private formatInputText(
    key: string,
    val: string,
    index: number,
    onBlur: boolean
  ): void {
    //listen to all controls in form group
    const column = this.columns.find(column => column.shortName === key);
    const formControl = (
      (this.scheduleArray as FormArray).controls[index] as FormControl
    ).get(key);
    if (val) {
      const decimalPlaces = onBlur ? column.dp : (column.dp || 0) + 2;
      const formattedValue = ScheduleUtilities.formatValue(
        column.format,
        val,
        decimalPlaces,
        onBlur
      );
      if (val === formattedValue) {
        if (column.format === 'percentage') {
          this.textBoxCaretRestrictionHandler(val);
        }
        this.valueChanged(val, key, index, '');
      } else {
        formControl.setValue(formattedValue, { emitEvent: false });
        this.valueChanged(formattedValue, key, index, '');
      }
    }
  }

  textBoxCaretRestrictionHandler(val: string) {
    // reset textbox caret to before the % symbol position
    const indexLocator = Utilities.getIndexOfSeparator(val, '%');
    if (indexLocator > -1) {
      this.renderer.setProperty(
        this.selectedFieldElem,
        'selectionEnd',
        indexLocator
      );
    }
  }

  onPaste(event: ClipboardEvent, index: number, columnIndex: number) {
    const textPasted = event.clipboardData.getData('text/plain');
    event.preventDefault();
    const splitTextRow = textPasted.trimEnd().split(/\r?\n/); // split the text by new lines
    if (splitTextRow.length) {
      const columnsToUse = this.columns.slice(columnIndex, this.columns.length); // the columns we reference that we will use later
      splitTextRow.forEach((row, rowIndex) => {
        // run through each row
        if (index + rowIndex < (this.scheduleArray as FormArray).length) {
          // if we are past the available rows in the form don't paste anything
          row
            .split('\t')
            .forEach((columnValue: any, columnForIndex: number) => {
              // run through each column
              if (columnForIndex < columnsToUse.length) {
                const columnShortName = columnsToUse[columnForIndex].shortName; // get the short key
                const field = (this.scheduleArray as FormArray).controls[
                  index + rowIndex
                ] as FormControl;

                if (this.isColumnADate(columnShortName)) {
                  if (
                    new RegExp(
                      '(^(0?[1-9]|[12][0-9]|3[01])-(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)-([0-9]{2})?$)$',
                      'i'
                    ).test(columnValue)
                  ) {
                    // if date is in format of 21-jan-21 turn into 2021-01-21
                    const splitDate = columnValue.split('-');
                    columnValue =
                      splitDate[0] +
                      ' ' +
                      splitDate[1] +
                      ' ' +
                      '20' +
                      splitDate[2];
                    columnValue = DateUtilities.getDateInISOFormat(columnValue);
                  } else if (DateUtilities.isDateOfDashedFormat(columnValue)) {
                    // if in format 21/01/2021 convert it to 2021-01-21
                    columnValue = DateUtilities.restructureDate(columnValue);
                  }
                }

                if (
                  columnValue.includes(',') &&
                  !isNaN(columnValue.replace(/,/g, ''))
                ) {
                  // we don't want to store ',' in the form so remove them
                  columnValue = +columnValue.replace(/,/g, '');
                }
                if (
                  field &&
                  field.enabled &&
                  field.get(columnShortName) &&
                  columnValue !== field.get(columnShortName).value
                ) {
                  field.get(columnShortName).setValue(columnValue);
                  this.futureDatePrep(columnShortName, index + rowIndex);
                }

                if (rowIndex === splitTextRow.length - 1) {
                  // changing fields on the last pasted row should affect the subsequent rows
                  this.valueChanged(
                    columnValue,
                    columnShortName,
                    index + rowIndex,
                    'continueFromPaste'
                  );
                }
                field.markAsDirty();
              }
            });
        }
      });
    }
  }

  isColumnADate(columnShortName: string) {
    return (
      this.columns.find(column => column.shortName === columnShortName)
        .format === 'date'
    );
  }

  valueChanged(val: any, column: string, index: number, inputType: any) {
    // If they changed a column field, then alter the field for all the other occurrence's of the index afterwards
    // to be exactly that value that was changed
    if (
      !this.isColumnADate(column) &&
      column !== 'isExerciseDate' &&
      column !== 'fixing' &&
      inputType !== 'insertFromPaste' // if the value changing is not a paste
    ) {
      for (let i = index; i < this.scheduleArray.value.length; i++) {
        const rowCtrl = (this.scheduleArray as FormArray).controls[
          i
        ] as FormControl;

        const oldValue = rowCtrl.get(column).value;

        if (oldValue !== val) {
          const field = rowCtrl.get(column);
          field.setValue(val, { emitEvent: false });
          field.markAsDirty();
          // we need to mark the item as dirty, so it can be recognized as being changed
        }

        if (column === 'strike') {
          rowCtrl.get('koBarrier')?.updateValueAndValidity();
          rowCtrl.get('kiBarrier')?.updateValueAndValidity();
        }
      }
    }

    this.futureDatePrep(column, index);
  }

  futureDatePrep(column: string, index: number) {
    const scheduleRow = (this.scheduleArray as FormArray).controls[
      index
    ] as FormControl;
    if (column === 'expiryDate' || column === 'fixingDate') {
      const expiryControl = scheduleRow.get(column);

      const settlementControl = scheduleRow.get('settlementDate');

      if (
        expiryControl.valid ||
        DateUtilities.isValidLag(expiryControl.value) ||
        DateUtilities.isCorrectDateFormat(expiryControl.value)
      ) {
        const payLoad = DateUtilities.dateOrLagToPayload(
          expiryControl.value,
          this.originalFormData.cross,
          '',
          this.productsConfigService.getCutOffCode(
            this.legData.get('cutOff')?.value
          ),
          this.productsConfigService.getFixingSourceCode(
            this.legData.get('fixingSource')?.value
          )
        );

        this.callCalculateFutureDate(expiryControl, settlementControl, payLoad);
      }
    } else if (
      column === 'settlementDate' &&
      !this.columns.filter(column => column.shortName === 'fixingDate')
        .length &&
      !this.columns.filter(column => column.shortName === 'expiryDate').length
    ) {
      const settlementControl = scheduleRow.get(column);

      if (
        settlementControl.valid ||
        DateUtilities.isValidLag(settlementControl.value) ||
        DateUtilities.isCorrectDateFormat(settlementControl.value)
      ) {
        const payLoad = DateUtilities.dateOrLagToPayload(
          settlementControl.value,
          this.originalFormData.cross,
          '',
          this.productsConfigService.getCutOffCode(
            this.legData.get('cutOff')?.value
          ),
          this.productsConfigService.getFixingSourceCode(
            this.legData.get('fixingSource')?.value
          )
        );

        this.callCalculateFutureDate(null, settlementControl, payLoad);
      }
    }
  }

  private callCalculateFutureDate(
    expiryControl: AbstractControl,
    settlementControl: AbstractControl,
    payload: CalculateFutureDatePayload
  ) {
    this.legService.calculateFutureDate(payload).then(p => {
      p.subscribe((resp: any) => {
        if (resp.expirationDate || resp.settlementDate) {
          if (resp.expirationDate && expiryControl) {
            // if the date from server is the same as the date in the textbox don't set again,
            // as pipe wouldn't transform the data
            if (resp.expirationDate !== expiryControl.value) {
              const formattedDate = ScheduleUtilities.formatValue(
                'date',
                resp.expirationDate,
                null,
                false
              );
              expiryControl.setValue(formattedDate, { emitEvent: false });
              // expiryControl.updateValueAndValidity();
              expiryControl.markAsDirty(); // mark the expiryControl as dirty //
            }

            if (expiryControl.parent.controls['fixing']) {
              const disableFixing =
                new Date(resp.expirationDate).getTime() > new Date().getTime();

              if (disableFixing) {
                // if the fixing is in the past
                (
                  expiryControl.parent.controls['fixing'] as FormControl
                ).disable();
              } else {
                (
                  expiryControl.parent.controls['fixing'] as FormControl
                ).enable();
              }
            }
          }

          if (
            resp.settlementDate &&
            settlementControl &&
            resp.settlementDate !== settlementControl.value
          ) {
            // if we should also set the settlement date
            const formattedDate = ScheduleUtilities.formatValue(
              'date',
              resp.settlementDate,
              null,
              false
            );

            settlementControl.setValue(formattedDate, {
              emitEvent: false
            });
            settlementControl.markAsDirty();
          }
        } else {
          console.error('Unable to retrieve settlement date.');
        }
      });
    });
  }

  saveSchedule() {
    let anyChanges = false;
    let changedAttributes = []; // this array holds all attributes e.g. notional that has been changed

    if ((this.scheduleArray as FormArray).dirty) {
      // if the schedule has been edited
      (this.scheduleArray as FormArray).controls
        .filter(x => {
          return x.dirty === true;
        }) // loop through all items in the form array and take note of those that have changed
        .map((x: FormGroup) => {
          //for each form group loop through the keys
          Object.keys(x.controls).map(key => {
            // for each of the control's in the form group
            if (x.controls[key].dirty) {
              // if the key is dirty
              if (
                x.controls[key].value !==
                this.originalFormData[this.scheduleName][
                  x.controls['fixingNumber'].value - 1
                ][key] // if the key has changed values from its original
              ) {
                anyChanges = true;
                if (changedAttributes.indexOf(key) === -1) {
                  // if the key isn't already in the list of attributes that have been changed.
                  switch (key) {
                    case 'fixingDate':
                    case 'expiryDate':
                    case 'settlementDate':
                    case 'exerciseDate':
                      const frequencyUsed = this.frequencyUsed();
                      const [firstTypeOfDate, lastTypeOfDate] =
                        this.setDatesChanged(key);

                      if (x.controls['fixingNumber'].value === 1) {
                        if (!changedAttributes.includes(firstTypeOfDate)) {
                          // if the firstFixingDate is already not in the array, push it
                          changedAttributes.push(firstTypeOfDate);
                        }
                        if (
                          this.originalFormData[this.scheduleName].length === 1
                        ) {
                          // if only one fixing date is available, then it is also the last fixing date.
                          if (!changedAttributes.includes(lastTypeOfDate)) {
                            changedAttributes.push(lastTypeOfDate);
                          }
                        }
                      } else if (
                        x.controls['fixingNumber'].value ===
                        this.originalFormData[this.scheduleName].length
                      ) {
                        if (!changedAttributes.includes(lastTypeOfDate)) {
                          changedAttributes.push(lastTypeOfDate);
                        }
                      }

                      if (
                        frequencyUsed &&
                        !changedAttributes.includes(frequencyUsed)
                      ) {
                        changedAttributes.push(frequencyUsed);
                      }

                      break;
                    default:
                      if (!changedAttributes.includes(key)) {
                        changedAttributes.push(key); // push the key that has changed values
                      }
                      break;
                  }
                }
              }
            }
          });
        });
      this.legData.setControl(this.scheduleName, this.scheduleArray);
    }

    if (anyChanges) {
      this.fieldChanges.emit({
        changedAttributes,
        scheduleName: this.scheduleName
      }); // emit that the values that have changed
    }
    this.scheduleOpenChanged.emit(false);
  }

  setDatesChanged(key) {
    switch (key) {
      case 'settlementDate':
        return ['firstSettlementDate', 'lastSettlementDate'];
      case 'expiryDate':
        return ['firstExpirationDate', 'lastExpirationDate'];
      case 'fixingDate':
        return ['firstFixingDate', 'lastFixingDate'];
      case 'exerciseDate':
        return ['firstExerciseDate', ''];
      default:
        return [];
    }
  }

  isFlowAttribute(shortName: string): boolean {
    // if the column is part of the additional Columns return true
    return !!this.additionalColumns.filter(x => x.shortName === shortName)
      .length;
  }

  onCopyToClipboardClicked() {
    const table = this.elementRef.nativeElement.querySelector('#table-data');
    // copy the text. keep in text/plain, so that tabs and new lines stay
    copy(Utilities.tableToExcelFormat(table), { format: 'text/plain' });
  }

  modalChanged() {
    this.scheduleOpenChanged.emit(false);
  }

  public mouseEnterField(formCtrl: AbstractControl): void {
    this.hoveredError = formCtrl?.errors?.invalidEntry?.value;
  }

  public mouseLeaveField(): void {
    this.hoveredError = null;
  }

  public attributeBlur(
    val: string,
    column: string,
    index: number,
    inputType: any,
    item: AbstractControl
  ) {
    // when bluring a text box convert the attribute changed into its formatted version that depends on the
    // cross if possible

    const crossBelongsTo = Utilities.attributeBelongsToWhichCross(column);

    if (
      this.legData.get(crossBelongsTo) &&
      this.legData.get(crossBelongsTo).value !== null &&
      this.legData.get(crossBelongsTo).value !== undefined &&
      item.value !== this.filedOriginalFocusedValue
    ) {
      const formattedValue = this.productsConfigService.crossDecimalFormatter(
        val,
        this.legData.get(crossBelongsTo).value
      );
      item.setValue(formattedValue);
      this.valueChanged(formattedValue, column, index, inputType);
    }
    this.formatInputText(column, val, index, true);
  }

  private frequencyUsed():
    | 'fixedLegFrequency'
    | 'floatingLegFrequency'
    | 'exerciseFrequency'
    | 'frequency' {
    switch (this.scheduleName) {
      case 'fixedLegSchedule':
        return 'fixedLegFrequency';
      case 'floatingLegSchedule':
        return 'floatingLegFrequency';
      case 'exerciseSchedule':
        return 'exerciseFrequency';
      case 'schedule':
        return 'frequency';
      default:
        return null;
    }
  }
}
